import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { setSession } from '../lib/auth';
import logo from '../assets/logo.png';

const MIN_LENGTH = 8; // matches the server's rule

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [linkError, setLinkError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  // Validate the link up front, so an expired one is reported before the user
  // bothers typing a new password.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/auth/reset-password/${token}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.ok) setEmail(data.email || '');
        else setLinkError(data.message || 'This reset link is invalid or has expired.');
      } catch {
        if (!cancelled) setLinkError('Could not check this link. Please try again.');
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }

    setWorking(true);
    try {
      const res = await fetch(`/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || 'Could not reset your password.');

      // The reset signs them in, so go straight where they belong.
      setSession(data.token);
      navigate(
        data.needsSubscription ? `/subscribe/${data.vendorId}` : `/dashboard/${data.vendorId}`,
        { replace: true }
      );
    } catch (err) {
      setError(err.message || 'Could not reset your password.');
      setWorking(false);
    }
  };

  if (checking) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
      <Box component="img" src={logo} alt="Rewarding" sx={{ height: 34, mb: 4 }} />
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 420, p: { xs: 2.5, sm: 4 }, borderRadius: 4, border: '1px solid #eee' }}>
        {linkError ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 1 }}>
              Link expired
            </Typography>
            <Typography variant="body2" sx={{ color: '#777', mb: 3 }}>{linkError}</Typography>
            <Button fullWidth variant="contained" onClick={() => navigate('/forgot-password')}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.4, minHeight: 48, borderRadius: 2.5, bgcolor: '#1a1a2e', '&:hover': { bgcolor: '#2a2a4e' } }}>
              Request a new link
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={submit}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 0.5 }}>
              Choose a new password
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              {email ? `for ${email}` : 'Enter a new password for your account.'}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              autoComplete="new-password"
              autoFocus
              helperText={`At least ${MIN_LENGTH} characters`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Confirm new password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(''); }}
              autoComplete="new-password"
              sx={{ mb: 2.5 }}
            />

            <Button fullWidth type="submit" variant="contained" disabled={working}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.4, minHeight: 48, borderRadius: 2.5, bgcolor: '#1a1a2e', '&:hover': { bgcolor: '#2a2a4e' } }}>
              {working ? 'Saving…' : 'Reset password & sign in'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
